import React, { useEffect, useState } from 'react'
import Typography from '@mui/material/Typography'
import { Alert, MenuItem, Select, Switch } from '@mui/material'
import ButtonStartIcon from '../../../../../button/ButtonStartIcon'
import InputField from '../../../../../FormControl/input/InputField'
import Dialog from '../../../../../dialog/Dialog'
import useThirdPartyAccess from '../../../../../../hooks/setting/useThirdPartyAccess'

const PROVIDERS = [{ value: 'acer-oars', label: 'ACER OARS (PAT)' }]

const Field = ({ label, children }) => (
  <>
    <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
      {label}
    </Typography>
    <div className='col-span-3'>{children}</div>
  </>
)

const ThirdPartyAccessDialog = ({ show, onClose, type, data = {} }) => {
  const { errors: errorsData, create, update, remove } = useThirdPartyAccess()
  const [form, setInputForm] = useState({})
  const [errors, setInputErrors] = useState(errorsData)
  const [loading, toggleLoading] = useState(false)

  const onChange = (e) => {
    const { name, value } = e.target
    setInputForm({ ...form, [name]: value })
    setInputErrors({ ...errors, [name]: null })
  }

  const onSave = async (e) => {
    if (!['create', 'edit', 'delete'].includes(type)) {
      setInputErrors({ message: 'Invalid type' })
      return
    }
    e.preventDefault()
    toggleLoading(true)
    try {
      let result = { error: false }
      if (type === 'create') result = await create(form)
      if (type === 'edit') result = await update(form._id, form)
      if (type === 'delete') result = await remove(form._id)
      if (result.error) {
        setInputErrors(result)
      } else {
        onClose()
      }
    } finally {
      toggleLoading(false)
    }
  }

  useEffect(() => {
    setInputErrors(errorsData)
  }, [errorsData])

  useEffect(() => {
    if (data) {
      // Nunca precargamos la contraseña (write-only). `hasPassword` indica si ya hay una.
      setInputForm({
        provider: 'acer-oars',
        baseUrl: 'https://oars.acer.edu.au',
        enabled: true,
        ...data,
        password: ''
      })
    }
  }, [data])

  const Actions = (
    <>
      <ButtonStartIcon
        onClick={onClose}
        color='primary'
        variant='outlined'
        disabled={loading}
        startIcon={['fal', 'times']}
      >
        Cancel
      </ButtonStartIcon>
      {['create', 'edit', 'delete'].includes(type) && (
        <ButtonStartIcon
          onClick={onSave}
          color='primary'
          variant='contained'
          disabled={loading}
          loading={loading}
          startIcon={['fal', 'check']}
        >
          {type === 'delete' ? 'Confirm' : 'Save'}
        </ButtonStartIcon>
      )}
    </>
  )

  return (
    <Dialog
      id={`${type}-third-party-access`}
      show={show}
      title={`${type} external credential`}
      maxWidth='md'
      onClose={onClose}
      actions={Actions}
    >
      {type === 'delete' ? (
        <Typography>
          Are you sure you want to delete the credential: <b>{form.label || form.schoolSlug}</b>? This does
          not delete the data already imported.
        </Typography>
      ) : (
        <div className='flex flex-col gap-4 py-4'>
          <div className='grid grid-cols-4 items-center gap-4'>
            <Field label='Enabled'>
              <Switch
                id='enabled'
                color='primary'
                name='enabled'
                checked={form?.enabled ?? true}
                onChange={(e) =>
                  onChange({
                    target: { name: 'enabled', value: e.target.checked }
                  })
                }
              />
            </Field>

            <Field label='Provider'>
              <Select
                id='provider'
                name='provider'
                value={form.provider || 'acer-oars'}
                onChange={onChange}
                size='small'
                fullWidth
                disabled={type !== 'create'}
              >
                {PROVIDERS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </Field>

            <Field label='Label'>
              <InputField
                id='label'
                name='label'
                value={form.label || ''}
                placeholder='e.g. PAT — Boronia K-12'
                error={errors?.label}
                helperText={errors?.label}
                onChange={onChange}
              />
            </Field>

            <Field label='Base URL'>
              <InputField
                id='baseUrl'
                name='baseUrl'
                value={form.baseUrl || ''}
                placeholder='https://oars.acer.edu.au'
                error={errors?.baseUrl}
                helperText={errors?.baseUrl}
                onChange={onChange}
              />
            </Field>

            <Field label='School slug'>
              <InputField
                id='schoolSlug'
                name='schoolSlug'
                value={form.schoolSlug || ''}
                placeholder='e.g. boronia-k-12-college'
                error={errors?.schoolSlug}
                helperText={errors?.schoolSlug}
                onChange={onChange}
              />
            </Field>

            <Field label='Username'>
              <InputField
                id='username'
                name='username'
                value={form.username || ''}
                error={errors?.username}
                helperText={errors?.username}
                onChange={onChange}
              />
            </Field>

            <Field label='Password'>
              <InputField
                id='password'
                name='password'
                type='password'
                value={form.password || ''}
                placeholder={form.hasPassword ? '•••••••• (leave blank to keep current)' : 'Enter password'}
                error={errors?.password}
                helperText={errors?.password}
                onChange={onChange}
              />
            </Field>
          </div>
          <Typography variant='caption' color='text.secondary' className='px-2'>
            The password is encrypted at rest and never shown again. On edit, leave it blank to keep the
            stored one.
          </Typography>
        </div>
      )}
      {errors && errors.message && (
        <Alert severity='error' onClose={() => setInputErrors({ message: null })}>
          {errors.message}
        </Alert>
      )}
    </Dialog>
  )
}

export default ThirdPartyAccessDialog
